import { useState } from 'react'
import { AREA_DE_MEMBROS, FOLHA, type Bloco, type Trecho } from '../ajuda/conteudo'
import { secoesPara } from '../ajuda/secoes'
import { ancoraDaSecao } from '../ajuda/ancora'

// A folha de instrucoes DENTRO do app, para consultar no meio do mes.
//
// ⚠ Nao ha uma linha de texto neste arquivo: as palavras moram todas em
// src/ajuda/conteudo.ts, compartilhadas com a pagina solta que os 5 abrem
// antes de instalar. O que este arquivo faz e desenhar aquilo com a cara do
// app -- compartilha-se as strings, nunca o layout. Ha guarda cobrando.

const COR_DA_LUZ = {
  verde: 'bg-emerald-500',
  amarelo: 'bg-amber-500',
  vermelho: 'bg-red-600',
} as const

function TrechoView({ parte }: { parte: Trecho }) {
  if (typeof parte === 'string') return <>{parte}</>
  if ('forte' in parte) return <strong className="font-semibold">{parte.forte}</strong>
  if ('link' in parte)
    return (
      <a href={parte.url} target="_blank" rel="noopener" className="font-semibold text-teal-700 underline">
        {parte.link}
      </a>
    )
  // Texto que o proprio app escreve: em fonte de maquina nos dois
  // renderizadores, para o representante reconhecer a frase exata da tela.
  return (
    <span className="font-mono text-sm bg-slate-100 border border-slate-200 rounded px-1 break-words">
      {parte.tela}
    </span>
  )
}

function Partes({ lista }: { lista: Trecho[] }) {
  return (
    <>
      {lista.map((parte, i) => (
        <TrechoView key={i} parte={parte} />
      ))}
    </>
  )
}

function BlocoView({ bloco }: { bloco: Bloco }) {
  switch (bloco.tipo) {
    case 'paragrafo':
      return (
        <p className="text-sm text-slate-700">
          <Partes lista={bloco.partes} />
        </p>
      )

    case 'subtitulo':
      return <h4 className="text-sm font-semibold text-teal-700">{bloco.texto}</h4>

    case 'lista': {
      const classe = bloco.ordenada ? 'list-decimal' : 'list-disc'
      const Tag = bloco.ordenada ? 'ol' : 'ul'
      return (
        <Tag className={`${classe} pl-5 space-y-1 text-sm text-slate-700 marker:text-teal-700`}>
          {bloco.itens.map((item, i) => (
            <li key={i}>
              <Partes lista={item} />
            </li>
          ))}
        </Tag>
      )
    }

    case 'endereco':
      return (
        <div className="space-y-1">
          <a
            href={bloco.url}
            className="block bg-teal-50 text-teal-700 font-mono font-semibold text-center rounded-lg px-3 py-3 break-words"
          >
            {bloco.rotulo}
          </a>
          <p className="text-xs text-slate-500 text-center break-words">{bloco.legenda}</p>
        </div>
      )

    case 'atencao':
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-amber-700">
            {bloco.rotulo}
          </p>
          {bloco.blocos.map((interno, i) => (
            <BlocoView key={i} bloco={interno} />
          ))}
        </div>
      )

    case 'semaforo':
      return (
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {bloco.luzes.map((luz, i) => (
            <div key={i} className="py-2.5">
              <p className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${COR_DA_LUZ[luz.cor]}`} />
                <span className="font-mono text-sm font-semibold text-slate-800 break-words">
                  {luz.frase}
                </span>
              </p>
              <p className="text-sm text-slate-500 pl-4.5">{luz.querDizer}</p>
            </div>
          ))}
        </div>
      )
  }
}

export default function Ajuda() {
  // ⚠ 'app', nunca 'pagina': o recorte da pagina traz um aviso sobre abrir o
  // link fora do WhatsApp, que aqui dentro seria absurdo.
  const secoes = secoesPara(FOLHA, 'app')
  // Pedido dela 18/10: as secoes ficam FECHADAS, como as perguntas da pagina
  // de vendas; tocar no titulo abre o texto. O indice "Neste guia" saiu a
  // pedido dela (repetia os titulos dos cards).
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set())
  const alternar = (titulo: string) =>
    setAbertas((atual) => {
      const nova = new Set(atual)
      if (nova.has(titulo)) nova.delete(titulo)
      else nova.add(titulo)
      return nova
    })

  return (
    <section className="p-4 space-y-4" data-testid="tela-ajuda">
      <div className="space-y-2">
        <h2 className="text-lg font-bold text-slate-800">{FOLHA.titulo}</h2>
        {FOLHA.abertura.map((paragrafo, i) => (
          <p key={i} className="text-sm text-slate-600">
            <Partes lista={paragrafo} />
          </p>
        ))}
      </div>

      {secoes.map((secao) => {
        const aberta = abertas.has(secao.titulo)
        const idTexto = `texto-${ancoraDaSecao(secao.titulo)}`
        return (
          <div
            key={secao.titulo}
            id={ancoraDaSecao(secao.titulo)}
            className="bg-white rounded-xl shadow-sm p-4 space-y-3 scroll-mt-4"
          >
            {secao.parte && (
              <p className="text-xs font-mono uppercase tracking-wider text-slate-400">
                {secao.parte}
              </p>
            )}
            <h3>
              <button
                type="button"
                onClick={() => alternar(secao.titulo)}
                aria-expanded={aberta}
                aria-controls={idTexto}
                className="w-full min-h-11 font-medium text-slate-800 flex items-center gap-2 text-left"
              >
                {secao.passo !== undefined && (
                  <span className="shrink-0 w-6 h-6 rounded-full bg-teal-50 text-teal-700 font-mono text-xs font-bold flex items-center justify-center">
                    {secao.passo}
                  </span>
                )}
                <span className="flex-1">{secao.titulo}</span>
                <span aria-hidden="true" className="shrink-0 text-teal-700 text-lg leading-none">
                  {aberta ? '−' : '+'}
                </span>
              </button>
            </h3>
            {/* hidden, e nao "nao desenhar": o texto segue na pagina para a
                busca do navegador e para as guardas que leem a tela. */}
            <div id={idTexto} hidden={!aberta} className="space-y-3">
              {secao.blocos.map((bloco, i) => (
                <BlocoView key={i} bloco={bloco} />
              ))}
              {secao.aulas && secao.aulas.length > 0 && (
                <a
                  href={AREA_DE_MEMBROS}
                  target="_blank"
                  rel="noopener"
                  className="block rounded-lg bg-teal-50 px-3 py-2.5 space-y-1 text-sm font-semibold text-teal-700"
                >
                  <span className="block text-xs font-mono uppercase tracking-wider">🎓 Aula na Hotmart</span>
                  {secao.aulas.map((a) => (
                    <span key={a.aula} className="block">
                      {a.modulo} › {a.aula}
                    </span>
                  ))}
                </a>
              )}
              {secao.videos?.map((video) => (
                <a
                  key={video.url}
                  href={video.url}
                  target="_blank"
                  rel="noopener"
                  className="flex items-center gap-2 min-h-11 rounded-lg bg-teal-50 px-3 text-sm font-semibold text-teal-700"
                >
                  <span aria-hidden="true">▶</span> Vídeo: {video.titulo}
                </a>
              ))}
            </div>
          </div>
        )
      })}

      <p className="text-sm text-slate-500 text-center pt-2">{FOLHA.fecho}</p>
    </section>
  )
}
